// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { EducBox, ResultCard, fmt } from './shared';

// Importi base 2026 (INPS)
const BASE_MAX = 199.4;  // ISEE €0
const BASE_MIN = 57.0;   // ISEE ≥ €45.575
const ISEE_MAX = 45575;

const calcBasePerFiglio = (isee) => {
  const i = Math.min(Math.max(parseFloat(isee) || 0, 0), ISEE_MAX);
  return BASE_MAX - ((BASE_MAX - BASE_MIN) * i / ISEE_MAX);
};

const calcAssegno = ({ nFigli, isee, disabili, under3, monoGenitoriale, entrambiLavorano }) => {
  const base = calcBasePerFiglio(isee);
  const figli = Math.max(1, Math.min(10, parseInt(nFigli) || 1));
  const nDisabili = Math.min(parseInt(disabili) || 0, figli);
  const nUnder3 = Math.min(parseInt(under3) || 0, figli);
  const nNormali = figli - nDisabili - nUnder3;

  const rows = [];
  let totaleMensile = 0;

  // Figli normali
  for (let i = 0; i < Math.max(0, nNormali); i++) {
    let importo = base;
    if (monoGenitoriale) importo *= 1.35;
    const maggiorazioni = [];
    if (monoGenitoriale) maggiorazioni.push('Monogenitoriale +35%');
    const extraEntrambi = entrambiLavorano ? 30 : 0;
    importo += extraEntrambi;
    if (entrambiLavorano) maggiorazioni.push('+€30 (entrambi lavorano)');
    rows.push({ label: `Figlio ${i + 1}`, base, importo, maggiorazioni });
    totaleMensile += importo;
  }

  // Figli under 3
  for (let i = 0; i < Math.max(0, nUnder3); i++) {
    let importo = base * 1.5;
    const maggiorazioni = ['Under 3 anni +50%'];
    if (monoGenitoriale) { importo *= 1.35; maggiorazioni.push('Monogenitoriale +35%'); }
    const extraEntrambi = entrambiLavorano ? 30 : 0;
    importo += extraEntrambi;
    if (entrambiLavorano) maggiorazioni.push('+€30 (entrambi lavorano)');
    rows.push({ label: `Figlio under 3 ${i + 1}`, base, importo, maggiorazioni });
    totaleMensile += importo;
  }

  // Figli disabili
  for (let i = 0; i < Math.max(0, nDisabili); i++) {
    let importo = base * 1.85;
    const maggiorazioni = ['Disabilità +85%'];
    if (monoGenitoriale) { importo *= 1.35; maggiorazioni.push('Monogenitoriale +35%'); }
    const extraEntrambi = entrambiLavorano ? 30 : 0;
    importo += extraEntrambi;
    if (entrambiLavorano) maggiorazioni.push('+€30 (entrambi lavorano)');
    rows.push({ label: `Figlio con disabilità ${i + 1}`, base, importo, maggiorazioni });
    totaleMensile += importo;
  }

  return { rows, totaleMensile, totaleAnnuo: totaleMensile * 12, base };
};

const Slider = ({ label, value, onChange, min, max }) => (
  <div>
    <label className="text-[#888780] text-sm flex justify-between mb-1">
      {label} <strong className="text-[#E8E8E4]">{value}</strong>
    </label>
    <input type="range" min={min} max={max} value={value} onChange={e => onChange(parseInt(e.target.value))}
      className="w-full accent-[#C9A84C] cursor-pointer" />
    <div className="flex justify-between text-xs text-[#888780] mt-0.5"><span>{min}</span><span>{max}</span></div>
  </div>
);

const Toggle = ({ label, value, onChange, testId }) => (
  <div className="flex items-center justify-between py-2">
    <span className="text-sm text-[#888780]">{label}</span>
    <button onClick={() => onChange(!value)} data-testid={testId}
      className={`relative w-11 h-6 rounded-full transition-colors ${value ? 'bg-[#3D7A52]' : 'bg-[#252523] border border-white/[0.12]'}`}>
      <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${value ? 'left-5' : 'left-0.5'}`} />
    </button>
  </div>
);

const AssegnoUnico = () => {
  const [nFigli, setNFigli] = useState(1);
  const [isee, setIsee] = useState('25000');
  const [disabili, setDisabili] = useState(0);
  const [under3, setUnder3] = useState(0);
  const [monoGenitoriale, setMonoGenitoriale] = useState(false);
  const [entrambiLavorano, setEntrambiLavorano] = useState(true);

  const r = useMemo(() => calcAssegno({ nFigli, isee, disabili, under3, monoGenitoriale, entrambiLavorano }), [nFigli, isee, disabili, under3, monoGenitoriale, entrambiLavorano]);

  const maggiorazioniAttive = [];
  if (under3 > 0) maggiorazioniAttive.push(`${under3} figlio/i under 3: +50%`);
  if (disabili > 0) maggiorazioniAttive.push(`${disabili} figlio/i con disabilità: +85%`);
  if (monoGenitoriale) maggiorazioniAttive.push('Nucleo monogenitoriale: +35%');
  if (entrambiLavorano) maggiorazioniAttive.push('Entrambi i genitori lavorano: +€30/figlio');

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <EducBox text="L'Assegno Unico Universale è il sussidio mensile per ogni figlio a carico fino a 21 anni. L'importo dipende dall'ISEE familiare, dal numero di figli e da eventuali maggiorazioni. Importi 2026 aggiornati." />

      <div className="grid sm:grid-cols-2 gap-6 mb-6">
        <div className="space-y-4">
          <Slider label="Numero figli a carico" value={nFigli} onChange={setNFigli} min={1} max={6} />
          <div>
            <label className="text-[#888780] text-sm block mb-1">ISEE familiare (€)</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888780] text-sm">€</span>
              <input type="number" value={isee} onChange={e => setIsee(e.target.value)} className="w-full bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md pl-7 pr-3 py-2 text-sm" data-testid="isee-input-assegno" />
            </div>
          </div>
          <Slider label="Di cui figli under 3" value={under3} onChange={v => setUnder3(Math.min(v, nFigli))} min={0} max={nFigli} />
          <Slider label="Di cui figli con disabilità" value={disabili} onChange={v => setDisabili(Math.min(v, nFigli - under3))} min={0} max={Math.max(0, nFigli - under3)} />
        </div>
        <div className="space-y-1">
          <p className="text-xs text-[#888780] uppercase tracking-wider mb-2">Situazione familiare</p>
          <Toggle label="Genitore solo (monogenitoriale)" value={monoGenitoriale} onChange={setMonoGenitoriale} testId="toggle-mono" />
          <Toggle label="Entrambi i genitori lavorano" value={entrambiLavorano} onChange={setEntrambiLavorano} testId="toggle-entrambi" />
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4" data-testid="assegno-unico-results">
        <div>
          <div className="p-4 bg-[#252523] rounded-xl border border-white/[0.06]">
            <p className="text-xs text-[#888780] mb-1">Assegno mensile totale</p>
            <p className="text-2xl font-bold text-[#3D7A52]">€{fmt(r.totaleMensile)}</p>
          </div>
        </div>
        <ResultCard label="Per figlio (media)" value={`€${fmt(r.totaleMensile / Math.max(1, nFigli))}`} />
        <ResultCard label="Assegno annuale totale" value={`€${fmt(r.totaleAnnuo)}`} gold />
        <div className="p-4 bg-[#252523] rounded-xl border border-white/[0.06]">
          <p className="text-xs text-[#888780] mb-1">Maggiorazioni applicate</p>
          {maggiorazioniAttive.length > 0 ? (
            <ul className="space-y-0.5">
              {maggiorazioniAttive.map((m, i) => <li key={i} className="text-xs text-[#3D7A52]">✓ {m}</li>)}
            </ul>
          ) : <p className="text-xs text-[#888780]">Nessuna maggiorazione</p>}
        </div>
      </div>

      {/* Breakdown per figlio */}
      {r.rows.length > 0 && (
        <div className="sapiens-card p-5 mb-4">
          <p className="text-sm font-semibold text-[#E8E8E4] mb-3">Dettaglio per figlio</p>
          <table className="w-full">
            <thead><tr className="border-b border-white/[0.08]">
              <th className="text-left py-2 text-xs text-[#888780]">Figlio</th>
              <th className="text-right py-2 text-xs text-[#888780]">Quota base</th>
              <th className="text-left py-2 text-xs text-[#888780] pl-4">Maggiorazioni</th>
              <th className="text-right py-2 text-xs text-[#888780]">Totale/mese</th>
            </tr></thead>
            <tbody>
              {r.rows.map((row, i) => (
                <tr key={i} className="border-b border-white/[0.04]">
                  <td className="py-2.5 text-sm text-[#E8E8E4]">{row.label}</td>
                  <td className="py-2.5 text-sm text-right text-[#888780]">€{row.base.toFixed(1)}</td>
                  <td className="py-2.5 text-xs text-[#888780] pl-4">{row.maggiorazioni.join(', ') || '—'}</td>
                  <td className="py-2.5 text-sm text-right font-medium text-[#3D7A52]">€{fmt(row.importo)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="p-4 rounded-xl mb-4" style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.3)' }}>
        <p className="text-sm font-medium text-[#C9A84C] mb-1">Come richiedere l'Assegno Unico</p>
        <p className="text-sm text-[#888780] leading-relaxed">L'Assegno Unico viene erogato automaticamente dall'INPS se hai già presentato la DSU per l'ISEE. Domanda su <strong>myINPS</strong> o tramite CAF. Pagamento intorno al 15-20 del mese.</p>
      </div>

      <p className="text-xs text-[#888780] leading-relaxed" style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 12 }}>
        Importi 2026 aggiornati al decreto di rivalutazione ISTAT. Verifica gli importi definitivi su INPS.it. Soglia ISEE massima per importo pieno: €45.575.
      </p>
</motion.div>
  );
};

export default AssegnoUnico;
